import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateAvgChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';
import { getStats } from './avgThroughputUtil';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#00a2e3', '#007baa', '#015676'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background: ${point.color}"></span>${point.series.name}: ${point.point.y.toFixed(2)} item${point.point.y != 1 ? 's' : ''}</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};

const DEFAULT_POS = { x: 95, y: 85 };

export class AvgThroughput extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    this.chart = new Highcharts['Chart'](`avg_throughput_chart_container`, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat(this.props.dateFormat);
    }

    if (
      prevProps.data != this.props.data ||
      prevProps.dateFormat != this.props.dateFormat ||
      prevProps.timeUnit != this.props.timeUnit
    ) {
      this.calculateChartData();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Throughput Averages',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },          
        }
      },
      legend: {
        enabled: this.state.isFullSreen,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        labelFormatter: function () {
          return `
            <span class="color:#333333;font-size:12px;font-weight:normal">${this.name}</span>
          `;
        }
      },
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    });

    this.chart.reflow();
  };

  updateDateFormat(dateFormat) {
    const format = dateUtils.createHighchartFormats(dateFormat);
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: format.month,
          second: format.month,
          minute: format.month,
          hour: format.month,
          day: format.month,
          week: format.month,
          month: format.month
        }
      }
    });
  }

  calculateChartData() {
    const data = this.generateData(this.props.data);
    if (!data.mean.length) {
      return false;
    }

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }
    this.chart.addSeries(
      {
        name: 'Mean',
        data: data.mean
      },
      false
    );
    this.chart.addSeries(
      {
        name: 'Median',
        data: data.median
      },
      false
    );
    this.chart.addSeries(
      {
        name: 'Mode',
        data: data.mode
      },
      false
    );

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data.mean[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
  }

  generateData = data => {
    return getStats(data, this.props.timeUnit, this.props.excludeWeekendOpts, this.props.excludeZeroDays);
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <div className='box_white_title'>
            <h3>Throughput Averages</h3>
            <div className='right-actions'>
              <Button variant='secondary' size='sm'
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          
          <div id="avg_throughput_chart_container" />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'th')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    timeUnit: state.filters.th.timeUnit
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(AvgThroughput));
